import React, { useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { useGame, useGamePublishers, useMyGames } from "@seabrookstudios/pitch2table";
import {
  formatAge,
  GamesListFilters,
  PublisherGameStatusDescription,
  showAlarm,
} from "@seabrookstudios/pitch2table-core";
import { ListView } from "../core/ListView";
import { ListDetail } from "../web/ListDetail";
import { GameDetailFromRoute } from "./GameDetail";
import { NoItemsFound } from "../publisher/NoItemsFound";
import AddGameFullScreenDialog from "./AddGameFullScreenDialog";
import { Path } from "../Path";
import { FilterList } from "../core/FilterList";
import { useFiltersv2 } from "../core/useFilters";
import Article from "@mui/icons-material/Article";
import ListAlt from "@mui/icons-material/ListAlt";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import OutgoingMail from "@mui/icons-material/OutgoingMail";
import QuestionAnswer from "@mui/icons-material/QuestionAnswer";
import { FollowUpDuePill } from "./FollowUpDuePill";
import { getSeverity } from "./publisher-interest/follow-up-severity";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import Flaky from "@mui/icons-material/Flaky";
import Pending from "@mui/icons-material/Pending";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import { formatDuration, formatPlayerCount } from "./formatters";

/**
 * @type {import("@seabrookstudios/pitch2table-core").PublisherGameStatus[]}
 */
const STATUS_SEQUENCE = [
  "ShortListed",
  "PitchSent",
  "PitchReceived",
  "InProgress",
  "MaterialsRequested",
  "UnderConsideration",
  "InContractNegotiation",
];

/**
 * @type {Object<import("@seabrookstudios/pitch2table-core").PublisherGameStatus,React.ReactNode>}
 */
const StatusIcons = {
  ShortListed: ListAlt,
  PitchSent: OutgoingMail,
  PitchReceived: MarkEmailRead,
  InProgress: Pending,
  MaterialsRequested: QuestionAnswer,
  UnderConsideration: Flaky,
  InContractNegotiation: Article,
};

const EXCLUDED_STATUSES = new Set(["Declined", "Lost", "Withdrawn", "Signed"]);

const NoPitchingGames = () => (
  <NoItemsFound>
    <Typography align="center">No pitching games found.</Typography>
  </NoItemsFound>
);

const NoPitchingPublishers = ({ game }) => {
  return (
    <Alert severity="info">
      No active publisher submissions for {game.name}. Add publisher interest or move a publisher out of declined,
      withdrawn, signed or lost to see them here.
    </Alert>
  );
};

const getProgressIndex = (status) => {
  return STATUS_SEQUENCE.indexOf(status);
};

/**
 * Readiness matches game detail: materials use truthy sellsheet / rules / sizzle URLs; player count,
 * duration, and age use the same formatters as EditPlayersControl, EditDurationControl, EditAgeControl.
 *
 * @param {{ game: import("@seabrookstudios/pitch2table-core").GameSummary }} props
 */
const PitchReadinessChips = ({ game }) => {
  const { data: fullGameDetails } = useGame(game.id);

  const hasSellsheet = Boolean(fullGameDetails?.sellsheet);
  const hasRules = Boolean(fullGameDetails?.rules);
  const hasSizzle = Boolean(fullGameDetails?.sizzle);

  const pitchItems = [
    { key: "sellsheet", label: "Sellsheet", ok: hasSellsheet },
    { key: "rules", label: "Rules", ok: hasRules },
    { key: "sizzle", label: "Sizzle", ok: hasSizzle },
  ];

  const metaItems = [
    {
      key: "players",
      ok: Boolean(fullGameDetails?.playerCount),
      missingLabel: "Player Count",
      valueLabel: fullGameDetails?.playerCount ? `Players: ${formatPlayerCount(fullGameDetails.playerCount)}` : "",
    },
    {
      key: "duration",
      ok: Boolean(fullGameDetails?.duration),
      missingLabel: "Duration",
      valueLabel: fullGameDetails?.duration ? formatDuration(fullGameDetails.duration) : "",
    },
    {
      key: "age",
      ok: Boolean(fullGameDetails?.age),
      missingLabel: "Age",
      valueLabel: fullGameDetails?.age ? `Age: ${formatAge(fullGameDetails.age)}` : "",
    },
  ];

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" pt={1}>
      {pitchItems.map(({ key, label, ok }) => (
        <Tooltip key={key} title={ok ? `${label} ready` : `${label} missing`}>
          <Chip
            size="small"
            icon={ok ? <Check /> : <Close />}
            label={label}
            color={ok ? "primary" : "error"}
            variant={ok ? "filled" : "outlined"}
          />
        </Tooltip>
      ))}
      {metaItems.map(({ key, ok, missingLabel, valueLabel }) => (
        <Tooltip key={key} title={ok ? valueLabel : `${missingLabel} not set`}>
          <Chip
            size="small"
            icon={ok ? <Check /> : <Close />}
            label={ok ? valueLabel : missingLabel}
            color={ok ? "primary" : "error"}
            variant={ok ? "filled" : "outlined"}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

const PitchingGameProgress = ({ game }) => {
  const { data: publishers = [], isLoading, isError } = useGamePublishers(game.id);

  const visiblePublishers = useMemo(() => {
    return publishers
      .filter((publisher) => !EXCLUDED_STATUSES.has(publisher.status))
      .sort((a, b) => {
        const statusDiff = getProgressIndex(b.status) - getProgressIndex(a.status);
        if (statusDiff !== 0) {
          return statusDiff;
        }
        return a.publisherName.localeCompare(b.publisherName);
      });
  }, [publishers]);

  return (
    <Card variant="outlined">
      <CardHeader
        title={game.name}
        subheader={
          <Stack direction="column" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            {game.tag ? (
              <Typography variant="body2" color="text.secondary" component="span">
                {game.tag}
              </Typography>
            ) : null}
            <PitchReadinessChips game={game} />
          </Stack>
        }
        action={
          <Link to={Path.game(game.id).publishers()} component={RouterLink}>
            <ArrowForwardOutlined />
          </Link>
        }
      />
      <CardContent>
        {isLoading && (
          <Box py={2}>
            <CircularProgress size={20} />
          </Box>
        )}
        {isError && <Alert severity="error">Failed to load publisher submissions.</Alert>}
        {!isLoading && !isError && visiblePublishers.length === 0 && <NoPitchingPublishers game={game} />}
        {!isLoading && !isError && visiblePublishers.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableBody>
                {visiblePublishers.map((publisher) => {
                  const progressIndex = getProgressIndex(publisher.status);
                  const followUpDue = showAlarm(publisher.nextAction);

                  return (
                    <TableRow
                      key={publisher.publisherId}
                      hover
                      component={RouterLink}
                      to={Path.game(game.id).publisher(publisher.publisherId)}
                      sx={{
                        cursor: "pointer",
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": { color: "inherit" },
                      }}
                    >
                      <TableCell>{publisher.publisherName}</TableCell>
                      {STATUS_SEQUENCE.map((status, index) => {
                        const Icon = StatusIcons[status];
                        const isComplete = progressIndex >= index;

                        return (
                          <TableCell align="center" key={status} width={24}>
                            {Icon ? (
                              <Tooltip title={PublisherGameStatusDescription[status]}>
                                <Icon fontSize="small" color={isComplete ? "primary" : "disabled"} />
                              </Tooltip>
                            ) : (
                              <Box width={24} />
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" width={24}>
                        {followUpDue ? (
                          <FollowUpDuePill severity={getSeverity(publisher.nextAction)} size="small" />
                        ) : (
                          <ListItemIcon>
                            <Box width={24} />
                          </ListItemIcon>
                        )}
                      </TableCell>
                      <TableCell align="center" width={24}>
                        <ArrowForwardOutlined />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

const CustomWebFilters = GamesListFilters.filter((filter) => filter.label !== "DUE");

const PitchingList = () => {
  const gamesQuery = useMyGames();
  const { data: games = [], isLoading, isRefetching, isError, refetch } = gamesQuery;

  const filterConfiguration = useFiltersv2(gamesQuery, CustomWebFilters, "games");

  const pitchingGames = useMemo(() => {
    return games.filter((game) => game.status === "PitchingNow" && !game.archived);
  }, [games]);

  return (
    <ListView
      title="My Games"
      shown={pitchingGames.length}
      isLoading={isLoading}
      isRefetching={isRefetching}
      isError={isError}
      refetch={refetch}
      EmptyList={NoPitchingGames}
      Action={AddGameFullScreenDialog}
      Filters={() => (
        <FilterList
          filters={/** @type {any} */ (filterConfiguration.visibleFilters)}
          filter={filterConfiguration.filter}
          onFilterClicked={filterConfiguration.onFilterClicked}
        />
      )}
    >
      <Stack spacing={1} px={2} pb={2}>
        {pitchingGames.map((game) => (
          <PitchingGameProgress key={game.id} game={game} />
        ))}
      </Stack>
    </ListView>
  );
};

export const PitchingGamesListAndDetail = () => {
  return <ListDetail ListView={PitchingList} DetailView={GameDetailFromRoute} />;
};
